import type { LucideIcon } from 'lucide-react';
import { Blinds, Fan, Rows3, ToggleRight, Zap } from 'lucide-react';
import { ACCENT } from './accent';
import type { AccentKey } from './accent';
import { LOAD_TYPE } from './loadType';
import type { AcceptedLoadTypes } from './loadType';
import {
  DIN_LEVEL_PRESETS,
  DIN_VALUE,
  RELAY_VALUE,
  RELAY_VALUE_LABEL,
  TRAVEL,
  TRAVEL_LABEL,
  dinValueLabel,
  travelValue,
} from './dinValue';

/** Physical outputs on one rail. Every rail type today is a 4-channel module. */
const CHANNELS_PER_RAIL = 4;

/**
 * A DIN rail module type. `acceptedLoadTypes` is the single source of truth for
 * what may be wired to a rail channel — validation and the assignment pickers
 * both read it.
 */
export const RAIL_TYPE = {
  PHASE_CUT: 'phase_cut',
  ONE_TEN_V: 'one_ten_v',
  RELAY: 'relay',
  FAN: 'fan',
  CURTAIN: 'curtain',
} as const;

export type RailTypeKey = (typeof RAIL_TYPE)[keyof typeof RAIL_TYPE];

/** One button on a rail channel: what it says and what goes on the wire. */
export interface RailChannelAction {
  readonly label: string;
  readonly value: number;
}

export interface RailTypeSpec {
  readonly label: string;
  readonly shortLabel: string;
  readonly channels: number;
  readonly acceptedLoadTypes: AcceptedLoadTypes;
  readonly accent: AccentKey;
  readonly icon: LucideIcon;
  /**
   * What every channel of this family is sent when a technician asks for the
   * whole rail at rest: off for anything that switches or dims, stop for a
   * motor, which has no off. Kept here because the families do not agree on
   * which of their own actions is the quiet one.
   */
  readonly restValue: number;
  readonly restLabel: string;
  /**
   * The actions a technician reaches for on a channel of this family. A relay
   * takes 0 or 1; the dimmers and the fan take the shared 0–100 scale; a motor
   * takes a direction. Each family says so once, here.
   */
  readonly channelActions: readonly RailChannelAction[];
}

export const RAIL_TYPE_SPECS = {
  [RAIL_TYPE.PHASE_CUT]: {
    label: 'Forward phase cut dimmer',
    shortLabel: 'Phase cut',
    channels: CHANNELS_PER_RAIL,
    acceptedLoadTypes: [LOAD_TYPE.PHASE_CUT],
    accent: ACCENT.AMBER,
    icon: Zap,
    restValue: DIN_VALUE.MIN,
    restLabel: 'All off',
    channelActions: DIN_LEVEL_PRESETS.map((level) => ({
      label: dinValueLabel(level),
      value: level,
    })),
  },
  [RAIL_TYPE.ONE_TEN_V]: {
    label: '0–10V dimmer',
    shortLabel: '0–10V',
    channels: CHANNELS_PER_RAIL,
    acceptedLoadTypes: [LOAD_TYPE.ONE_TEN_V],
    accent: ACCENT.VIOLET,
    icon: Zap,
    restValue: DIN_VALUE.MIN,
    restLabel: 'All off',
    channelActions: DIN_LEVEL_PRESETS.map((level) => ({
      label: dinValueLabel(level),
      value: level,
    })),
  },
  [RAIL_TYPE.RELAY]: {
    label: 'Relay module',
    shortLabel: 'Relay',
    channels: CHANNELS_PER_RAIL,
    acceptedLoadTypes: [LOAD_TYPE.RELAY],
    accent: ACCENT.SKY,
    icon: ToggleRight,
    restValue: DIN_VALUE.MIN,
    restLabel: 'All off',
    channelActions: [
      { label: RELAY_VALUE_LABEL[RELAY_VALUE.OFF], value: RELAY_VALUE.OFF },
      { label: RELAY_VALUE_LABEL[RELAY_VALUE.ON], value: RELAY_VALUE.ON },
    ],
  },
  [RAIL_TYPE.FAN]: {
    label: 'Fan controller',
    shortLabel: 'Fan',
    channels: CHANNELS_PER_RAIL,
    acceptedLoadTypes: [LOAD_TYPE.FAN],
    accent: ACCENT.SKY,
    icon: Fan,
    restValue: DIN_VALUE.MIN,
    restLabel: 'All off',
    channelActions: DIN_LEVEL_PRESETS.map((level) => ({
      label: dinValueLabel(level),
      value: level,
    })),
  },
  [RAIL_TYPE.CURTAIN]: {
    label: 'Curtain controller',
    shortLabel: 'Curtain',
    channels: CHANNELS_PER_RAIL,
    acceptedLoadTypes: [LOAD_TYPE.CURTAIN],
    accent: ACCENT.TEAL,
    icon: Blinds,
    restValue: DIN_VALUE.MIN,
    restLabel: 'All stop',
    channelActions: [
      { label: TRAVEL_LABEL[TRAVEL.OPEN], value: travelValue(TRAVEL.OPEN) },
      { label: TRAVEL_LABEL[TRAVEL.STOP], value: travelValue(TRAVEL.STOP) },
      { label: TRAVEL_LABEL[TRAVEL.CLOSE], value: travelValue(TRAVEL.CLOSE) },
    ],
  },
} as const satisfies Record<RailTypeKey, RailTypeSpec>;

/** Declaration order used by the rail summary strip and the add-rail picker. */
export const RAIL_TYPE_KEYS = [
  RAIL_TYPE.PHASE_CUT,
  RAIL_TYPE.ONE_TEN_V,
  RAIL_TYPE.RELAY,
  RAIL_TYPE.FAN,
  RAIL_TYPE.CURTAIN,
] as const satisfies readonly RailTypeKey[];

/** Icon shown for "all rails" summaries. */
export const RAIL_ICON: LucideIcon = Rows3;

/** Runtime guard for values arriving from storage. */
export const isRailTypeKey = (value: string): value is RailTypeKey =>
  RAIL_TYPE_KEYS.some((key) => key === value);
